// Who an invoice goes to and who signs it off, for one partner or one deal.
//
// A partner's card lists the partner's own people. A deal's card lists who the
// deal's invoices ACTUALLY involve: its own people, and under them its
// partner's for every role the deal names nobody in. The partner's rows are
// read-only there, and they come from the deal read's
// `effective_billing_contacts`, which is where that rule is resolved once.

import { useState, type ReactNode } from "react";
import type { BillingContact, DealEffectiveBillingContact } from "@crm/shared";
import styles from "./BillingContactsCard.module.css";
import { Button } from "../../design/Button.js";
import { Card, CardHeader } from "../../design/Card.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { EmptyRow, Table, Td, Th } from "../../design/Table.js";
import { DeleteDialog } from "../../forms/DeleteDialog.js";
import { useBillingContactMutations } from "../../api/mutations.js";
import { useBillingContacts, type BillingContactOwner } from "../../api/queries.js";
import { billingContactRoleLabel } from "../../format/labels.js";
import { BillingContactFormDialog } from "./BillingContactFormDialog.js";

/** What a deal's card needs beyond its own rows. */
export interface InheritedBillingContacts {
  partnerName: string;
  /** The deal's `effective_billing_contacts`. */
  effective: readonly DealEffectiveBillingContact[];
}

export interface BillingContactsCardProps {
  owner: BillingContactOwner;
  /** The partner's name, or the deal's customer. */
  ownerName: string;
  /** Present on a deal's card only. */
  inherited?: InheritedBillingContacts;
  canEdit: boolean;
}

type Action =
  | { kind: "create" }
  | { kind: "edit"; contact: BillingContact }
  | { kind: "delete"; contact: BillingContact };

export function BillingContactsCard({
  owner,
  ownerName,
  inherited,
  canEdit,
}: BillingContactsCardProps): ReactNode {
  const query = useBillingContacts(owner);
  const mutations = useBillingContactMutations();
  const [action, setAction] = useState<Action | null>(null);
  const fromPartner = inherited?.effective.filter((contact) => contact.source === "partner") ?? [];
  const columns = canEdit ? 4 : 3;

  return (
    <Card className={styles.card}>
      <CardHeader
        title="Billing contacts"
        description={
          owner.kind === "partner"
            ? "Who this partner's invoices are sent to and who approves them. A deal can name its own."
            : "Who this deal's invoices are sent to and who approves them. Its own people replace its partner's, role by role."
        }
        {...(canEdit
          ? {
              actions: (
                <Button small onClick={() => setAction({ kind: "create" })}>
                  Add contact
                </Button>
              ),
            }
          : {})}
      />
      <QueryBoundary query={query} label="the billing contacts">
        {(page) => (
          <Table label={`Billing contacts for ${ownerName}`}>
            <thead>
              <tr>
                <Th>Role</Th>
                <Th>Name</Th>
                <Th>Email</Th>
                {canEdit && <Th />}
              </tr>
            </thead>
            <tbody>
              {page.items.length === 0 && fromPartner.length === 0 && (
                <EmptyRow
                  colSpan={columns}
                  title="Nobody on file"
                  description={`Nothing here says who ${ownerName}'s invoices go to.`}
                />
              )}
              {page.items.map((contact) => (
                <tr key={contact.id}>
                  <Td nowrap>{billingContactRoleLabel(contact.role)}</Td>
                  <Td strong>{contact.name ?? "—"}</Td>
                  <Td>{contact.email}</Td>
                  {canEdit && (
                    <Td>
                      <div className={styles.rowActions}>
                        <Button
                          small
                          variant="subtle"
                          aria-label={`Edit ${contact.email}`}
                          onClick={() => setAction({ kind: "edit", contact })}
                        >
                          Edit
                        </Button>
                        <Button
                          small
                          variant="subtle"
                          aria-label={`Remove ${contact.email}`}
                          onClick={() => setAction({ kind: "delete", contact })}
                        >
                          Remove
                        </Button>
                      </div>
                    </Td>
                  )}
                </tr>
              ))}
              {fromPartner.map((contact) => (
                <tr key={contact.id}>
                  <Td nowrap muted>
                    {billingContactRoleLabel(contact.role)}
                  </Td>
                  <Td muted>{contact.name ?? "—"}</Td>
                  <Td muted>{contact.email}</Td>
                  {canEdit ? (
                    <Td muted nowrap>{`From ${inherited?.partnerName ?? "the partner"}`}</Td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </QueryBoundary>

      {action?.kind === "create" && (
        <BillingContactFormDialog
          owner={owner}
          ownerName={ownerName}
          onClose={() => setAction(null)}
        />
      )}
      {action?.kind === "edit" && (
        <BillingContactFormDialog
          owner={owner}
          ownerName={ownerName}
          contact={action.contact}
          onClose={() => setAction(null)}
        />
      )}
      {action?.kind === "delete" && (
        <DeleteDialog
          open
          onClose={() => setAction(null)}
          title="Remove billing contact"
          description={`${action.contact.name ?? action.contact.email} will no longer be on ${ownerName}'s invoices.`}
          record={action.contact}
          remove={(id, version) => mutations.remove.mutateAsync({ id, version })}
          fetchCurrent={mutations.fetchCurrent}
          onDeleted={() => setAction(null)}
        />
      )}
    </Card>
  );
}
